import React, { useState } from 'react';
import { Box, Typography, Card, CardContent, Button, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import ProductSetupWizard from './prediction/wizards/ProductSetupWizard'; // 🚀 修正：パス更新

/**
 * 製品設定が未設定の場合の作成プロンプト
 * @param {string}  productName  表示する製品名
 * @param {boolean} isHighlighted 新規登録直後など、強調表示させたい場合に true
 * @param {number}  clientId     紐付ける顧客 ID（ウィザードに伝搬）
 * @param {number}  productId    紐付ける製品 ID（ウィザードに伝搬）
 * @param {Function} onCreateClick 完了時に呼ばれるハンドラー (productData)=>Promise
 */
export default function ProductCreatePrompt({
  productName,
  onCreateClick,
  isHighlighted = false,
  clientId,
  productId
}) {
  const [wizardOpen, setWizardOpen] = useState(false);

  /* -------------------------------------------------------------- */
  /* ハンドラー                                                     */
  /* -------------------------------------------------------------- */
  const handleCreateWithWizard = () => setWizardOpen(true);

  const handleWizardComplete = async (productData) => {
    try {
      await onCreateClick(productData); // 親で POST 実行
      setWizardOpen(false);
    } catch (err) {
      // エラーハンドリングは親に委譲
    }
  };

  /* -------------------------------------------------------------- */
  /* レンダリング                                                   */
  /* -------------------------------------------------------------- */
  return (
    <>
      <Card
        elevation={isHighlighted ? 3 : 2}
        sx={{
          borderRadius: 2,
          mb: 3,
          overflow: 'hidden',
          border: isHighlighted ? '2px solid' : 'none',
          borderColor: 'primary.main',
          transition: 'all 0.3s ease'
        }}
      >
        {isHighlighted && (
          <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: 0 }}>
            顧客の登録が完了しました 🎉
          </Alert>
        )}

        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom color="primary" fontWeight="medium">
            {productName} の残量予測を設定しませんか？
          </Typography>

          <Typography variant="body1" color="text.secondary" paragraph>
            3 つの情報を入力するだけで、毎日更新される残量予測が利用できます。
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center', minWidth: 100 }}>
              <Typography variant="h6">🛢️</Typography>
              <Typography variant="body2" color="text.secondary">
                タンク容量
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', minWidth: 100 }}>
              <Typography variant="h6">📊</Typography>
              <Typography variant="body2" color="text.secondary">
                月間消費量
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', minWidth: 100 }}>
              <Typography variant="h6">📏</Typography>
              <Typography variant="body2" color="text.secondary">
                現在の残量
              </Typography>
            </Box>
          </Box>

          <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2">
              <strong>ポイント:</strong> 正確でなくても大体の値で始められます。
            </Typography>
          </Alert>

          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateWithWizard}
            size="large"
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              animation: isHighlighted ? 'pulse 2s infinite' : 'none',
              '@keyframes pulse': {
                '0%': { boxShadow: '0 0 0 0 rgba(33, 150, 243, 0.4)' },
                '70%': { boxShadow: '0 0 0 10px rgba(33, 150, 243, 0)' },
                '100%': { boxShadow: '0 0 0 0 rgba(33, 150, 243, 0)' }
              }
            }}
          >
            ガイドに従って設定する
          </Button>

          <Typography variant="caption" display="block" sx={{ mt: 2 }} color="text.secondary">
            約 2 分で完了・機能設定は後から変更可能
          </Typography>
        </CardContent>
      </Card>

      {/* 🚀 ウィザード：新規顧客用セットアップ */}
      <ProductSetupWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onComplete={handleWizardComplete}
        productName={productName}
        clientId={clientId}
        productId={productId}
        showTankSetupStep={true} // 🚀 新規作成時はタンク設置確認を表示
      />
    </>
  );
}